import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Building2, Images, Mail, Clock, ArrowRight } from "lucide-react";
import { adminGetStats, adminGetContact } from "@/lib/api";
import { AdminShell } from "@/components/admin/AdminShell";
import { StatCard } from "@/components/site/StatCard";

export default function AdminDashboard() {
  const { data: stats } = useQuery({ queryKey: ["admin-stats"], queryFn: adminGetStats });
  const { data: messages = [] } = useQuery({ queryKey: ["admin-contact"], queryFn: adminGetContact });

  return (
    <AdminShell title="Dashboard">
      <div data-testid="admin-dashboard-stats" className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Brands" value={stats?.brands ?? "—"} note={`${stats?.visibleBrands ?? 0} published`} />
        <StatCard label="Coming Soon" value={stats?.comingSoon ?? "—"} note="in development" />
        <StatCard label="Media" value={stats?.media ?? "—"} note="files stored" />
        <StatCard label="Messages" value={stats?.messages ?? "—"} note="enquiries received" />
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="rounded-[14px] border" style={{ background: "var(--surface)", borderColor: "var(--card-border)" }}>
            <div className="flex items-center justify-between border-b px-5 py-4" style={{ borderColor: "var(--card-border)" }}>
              <h2 className="text-sm font-semibold" style={{ color: "var(--text)" }}>Recent Enquiries</h2>
              <Link to="/admin/messages" className="inline-flex items-center gap-1 text-xs" style={{ color: "var(--accent-fg)" }}>View all <ArrowRight size={13} /></Link>
            </div>
            <div data-testid="admin-dashboard-activity-table">
              {messages.length === 0 ? (
                <p className="px-5 py-8 text-center text-sm" style={{ color: "var(--muted)" }}>No enquiries yet.</p>
              ) : (
                <ul>
                  {messages.slice(0, 5).map((m) => (
                    <li key={m.id} className="flex items-center justify-between border-b px-5 py-3 last:border-0" style={{ borderColor: "var(--card-border)" }}>
                      <div className="min-w-0">
                        <p className="truncate text-sm" style={{ color: "var(--text)" }}>{m.name} <span style={{ color: "var(--muted)" }}>• {m.category}</span></p>
                        <p className="truncate text-xs" style={{ color: "var(--muted)" }}>{m.email}</p>
                      </div>
                      <span className="flex shrink-0 items-center gap-1 text-xs" style={{ color: "var(--fg-subtle)" }}><Clock size={12} /> {new Date(m.createdAt).toLocaleDateString()}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <p className="motif-caps">Quick Actions</p>
          {[
            { to: "/admin/brands", label: "Manage Brands", icon: Building2 },
            { to: "/admin/media", label: "Media Library", icon: Images },
            { to: "/admin/messages", label: "View Messages", icon: Mail },
          ].map((a) => (
            <Link key={a.to} to={a.to} className="flex items-center gap-3 rounded-[12px] border px-4 py-3.5 text-sm transition-colors"
              style={{ background: "var(--surface)", borderColor: "var(--card-border)", color: "var(--text)" }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--accent)")}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--card-border)")}>
              <a.icon size={17} style={{ color: "var(--accent)" }} /> {a.label}
            </Link>
          ))}
          <div className="rounded-[12px] border border-dashed p-4" style={{ borderColor: "var(--card-border)" }}>
            <p className="text-xs" style={{ color: "var(--muted)" }}>Analytics integration is planned. Traffic and engagement metrics will appear here.</p>
          </div>
        </div>
      </div>
    </AdminShell>
  );
}
